import { useEffect, useState } from 'react';
import ExcelJS from 'exceljs';
import './FormsSummary.css';
import CanvasJSReact from '@canvasjs/react-charts';
import FormsSummaryItem from '../FormsSummaryItem/FormsSummaryItem';
import { capitalizeFirstLetter } from '../../utilities/services/capitalizeFirstLetter';
import { preferenceOptions } from '../../utilities/services/preferencesOptions';
import { getIntakeForms } from '../../utilities/api/allForms';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function tableHeader() {
  return (
    <thead className='header'>
      <tr>
        <th className='projectNumber'>
            Project Number
        </th>
        <th className='titleOfInvention'>
          Title
        </th>
        <th className='service'>
          Service
        </th>
        <th className='client'>
          Client
        </th>
        <th className='createdAt'>
          Creation
        </th>
        <th className='updatedAt'>
          Last Update
        </th>
        <th className='status'>
          Status
        </th>
        <th className='submissionDate'>
          Submission
        </th>
        <th className='approvalDate'>
          Approval
        </th>
      </tr>
    </thead>
  )
}


export default function FormsSummary({formsData, services, handleDropdownClick}) {
  const [annualData, setAnnualData] = useState([])
  const [formsByService, setFormsByService] = useState([])
  const [formsByServicePercent, setFormsByServicePercent] = useState([])
  const [isExportingSheet, setIsExportingSheet] = useState(false)
  const [exportSheetColumns, setExportSheetColumns] = useState([])

  const formsByServiceColumnChartOptions = {
    title: {
      // text: "Forms by Service"
    },
    theme: "light2",
    toolTip: {
      shared: true,
      contentFormatter: function (e) {
        let content = ''
        e.entries.forEach(entry => {
          content += `<strong>${entry.dataPoint.fullName}</strong>: ${entry.dataPoint.y} ${entry.dataPoint.y === 1 ? 'Form' : 'Forms'}<br>`
        })
        return content
      }
    },
    dataPointMaxWidth: 60,
    colorSet: "colorSet1",
    axisY: {
      title: "Quantity",
    },
    data: [{
      type: "column",
      // toolTipContent: "<b>{fullName}</b>: {y}",
      dataPoints: [
      ...formsByService
      ]
    }]
  }

  const areaChartOptions = {
    animationEnabled: true,
    theme: "light2",
    title: {
        // text: "Forms Created Monthly" 
    },
    axisX: {
        valueFormatString: "MMM",
        intervalType: "month",
        interval: 1
    },
    axisY: {
        title: "Quantity",
        // suffix: "°F"
    },
    toolTip: {
      shared: true,
      contentFormatter: function (e) {
        let content = ''
        e.entries.forEach(entry => {
          content += `<strong>${entry.dataSeries.name}</strong>: ${entry.dataPoint.y} ${entry.dataPoint.y === 1 ? 'Form' : 'Forms'}<br>`
        })
        return content
      }
    },
    legend: {
        cursor: "pointer",
        itemclick: function (e) {
            if (typeof (e.dataSeries.visible) === "undefined" || e.dataSeries.visible) {
                e.dataSeries.visible = false;
            } else {
                e.dataSeries.visible = true;
            }
            e.chart.render();
        }
    },
    data: annualData
  };

  const formsByServicePieChartOptions = {
    exportEnabled: true,
    animationEnabled: true,
    title: {
      // text: "Forms By Servive"
    },
    data: [{
      type: "pie",
      startAngle: 75,
      toolTipContent: "<b>{fullName}</b>: {y}%",
      // showInLegend: "true",
      // legendText: "{label}",
      indexLabelFontSize: 14,
      indexLabel: "{label} - {y}%",
      dataPoints: [
        ...formsByServicePercent
      ]
    }]
  }

  const handleExportSheetColumnChange = (evt) => {
    const column = evt.target.value
    if (exportSheetColumns.includes(column)) {
      setExportSheetColumns(exportSheetColumns.filter(item => item !== column))
    } else {
      setExportSheetColumns([...exportSheetColumns, column])
    }
  }

  const createExcelSheet = async () => {
    setIsExportingSheet(true)
    try {
      const forms = await getIntakeForms()
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Forms');
      sheet.columns = [
        exportSheetColumns.includes('projectNumber') && { header: 'PROJECT NUMBER', key: 'projectNumber', width: 20 },
        exportSheetColumns.includes('service') && { header: 'SERVICE', key: 'service', width: 20 },
        exportSheetColumns.includes('title') && { header: 'TITLE', key: 'title', width: 20 },
        exportSheetColumns.includes('companyName') && { header: 'CLIENT', key: 'companyName', width: 20 },
        exportSheetColumns.includes('status') && { header: 'STATUS', key: 'status', width: 20 },
        exportSheetColumns.includes('submissionDate') && { header: 'SUBMISSION', key: 'submissionDate', width: 20 },
        exportSheetColumns.includes('approvalDate') && { header: 'APPROVAL', key: 'approvalDate', width: 20 },
        exportSheetColumns.includes('createdAt') && { header: 'CREATION', key: 'createdAt', width: 20 },
        exportSheetColumns.includes('updatedAt') && { header: 'LAST UPDATE', key: 'updatedAt', width: 20 }
      ];
      sheet.getRow(1).font = { bold: true, size: 14 };
      forms.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).forEach(form => {
        sheet.addRow({
          projectNumber: form.projectNumber,
          service: form.service,
          title: form.title? capitalizeFirstLetter(form.title):'---',
          companyName: capitalizeFirstLetter(form.companyName),
          status: form.status,
          submissionDate: form.submissionDate? new Date(form.submissionDate).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }):'---',
          approvalDate: form.approvalDate? new Date(form.approvalDate).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }):'---',
          createdAt: new Date(form.createdAt).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }),
          updatedAt: new Date(form.updatedAt).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
        })
      })
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Forms.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error)
    }
    setIsExportingSheet(false)
  }

  useEffect(() => {
    const currentDate = new Date();
    const tempAnnualData = formsData.annualData.map(data => {
      return {
          type: "area",
          name: (data.year).toString(),
          showInLegend: true,
          xValueFormatString: "MMMM",
          yValueFormatString: "#,##0",
          // Use default year of 2024, so that line charts are aligned
          dataPoints: [
            { date: new Date(data.year, 0), x: new Date(2024, 0), y: data.monthlyData.JAN },
            { date: new Date(data.year, 1), x: new Date(2024, 1), y: data.monthlyData.FEB },
            { date: new Date(data.year, 2), x: new Date(2024, 2), y: data.monthlyData.MAR },
            { date: new Date(data.year, 3), x: new Date(2024, 3), y: data.monthlyData.APR },
            { date: new Date(data.year, 4), x: new Date(2024, 4), y: data.monthlyData.MAY },
            { date: new Date(data.year, 5), x: new Date(2024, 5), y: data.monthlyData.JUN },
            { date: new Date(data.year, 6), x: new Date(2024, 6), y: data.monthlyData.JUL },
            { date: new Date(data.year, 7), x: new Date(2024, 7), y: data.monthlyData.AUG },
            { date: new Date(data.year, 8), x: new Date(2024, 8), y: data.monthlyData.SEP },
            { date: new Date(data.year, 9), x: new Date(2024, 9), y: data.monthlyData.OCT },
            { date: new Date(data.year, 10), x: new Date(2024, 10), y: data.monthlyData.NOV },
            { date: new Date(data.year, 11), x: new Date(2024, 11), y: data.monthlyData.DEC }
          ].filter(dataPoint => currentDate >= dataPoint.date).map(dataPoint => {
            return {x: dataPoint.x, y: dataPoint.y}
          })
        }
    })
    setAnnualData(tempAnnualData)

    let totalForms = 0

    Object.keys(formsData.formsByService).forEach(service => {
      totalForms += formsData.formsByService[service]
    })
  
    const tempFormsByService = Object.keys(formsData.formsByService).map(service => {
      return {label: service, y: formsData.formsByService[service], fullName: services.find(s => s.code === service).name}
    })
    setFormsByService(tempFormsByService)

    const tempFormsByServicePercent = tempFormsByService.map(service => {
      return {label: service.label, y: Math.round((service.y / totalForms) * 100), fullName: services.find(s => s.code === service.label).name}
    })
    setFormsByServicePercent(tempFormsByServicePercent)
    // eslint-disable-next-line
  }, [formsData])


  return (
    <div className='FormsSummary'>
      <div className='exportFormsContainer dropdown'>
        <button className='exportForms' title='Export Forms to Spreadsheet' id='exportFormsDropDownToggle' data-title='Export Forms' data-bs-toggle="dropdown" data-bs-auto-close='outside' aria-expanded="false">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="20px" fill='#FFFFFF'>
            <path d="M 28.90625 1.96875 C 28.863281 1.976563 28.820313 1.988281 28.78125 2 L 11.5 2 C 9.585938 2 8 3.558594 8 5.46875 L 8 43.90625 C 8 46.160156 9.867188 48 12.125 48 L 37.875 48 C 40.132813 48 42 46.160156 42 43.90625 L 42 15.1875 C 42.027344 15.054688 42.027344 14.914063 42 14.78125 L 42 14.5 C 42.007813 14.234375 41.90625 13.972656 41.71875 13.78125 L 30.21875 2.28125 C 30.027344 2.09375 29.765625 1.992188 29.5 2 L 29.1875 2 C 29.097656 1.976563 29 1.964844 28.90625 1.96875 Z M 11.5 4 L 28 4 L 28 12.34375 C 28 14.355469 29.644531 16 31.65625 16 L 40 16 L 40 43.90625 C 40 45.074219 39.054688 46 37.875 46 L 12.125 46 C 10.945313 46 10 45.074219 10 43.90625 L 10 5.46875 C 10 4.644531 10.660156 4 11.5 4 Z M 30 4.9375 L 39.0625 14 L 31.65625 14 C 30.722656 14 30 13.277344 30 12.34375 Z M 17 24 L 17 38 L 33 38 L 33 24 Z M 19 26 L 24 26 L 24 28 L 19 28 Z M 26 26 L 31 26 L 31 28 L 26 28 Z M 19 30 L 24 30 L 24 32 L 19 32 Z M 26 30 L 31 30 L 31 32 L 26 32 Z M 19 34 L 24 34 L 24 36 L 19 36 Z M 26 34 L 31 34 L 31 36 L 26 36 Z"/>
          </svg>
          <span>
            &nbsp;&nbsp;Export Forms
          </span>
        </button>
        <div className='dropdown-menu exportFormsDropDown' aria-labelledby="exportFormsDropDownToggle" onClick={handleDropdownClick}>
          <div className='description'>Select Fields to Export</div>
          <ul>
          { Object.keys(preferenceOptions['forms']).map((option, index) => (
            <li key={index} className='dropdown-item'>
              <div className='form-check'>
                  <input className='form-check-input' 
                    id={`forms-${option}-${index}`} 
                    type="checkbox" 
                    defaultChecked={false} 
                    value={option}
                    disabled={isExportingSheet}
                    onChange={handleExportSheetColumnChange} 
                  />
                  <label className='form-check-label' htmlFor={`forms-${option}-${index}`}>
                      {preferenceOptions['forms'][option]}
                  </label>
              </div>
            </li>
            ))}
          </ul>
          <div className='buttonContainer'>
            <button className='exportForms' onClick={createExcelSheet} disabled={isExportingSheet || exportSheetColumns.length===0}>
              Export
            </button>
          </div>
        </div>
        
      </div>
      <div className='annualFormsInfo'>
        <div className='title1'>Monthly Data</div>
        <CanvasJSChart options = {areaChartOptions} />
      </div>
      <div className='forms'>
        <div className='title1'>Most Recent Forms</div>
        <div className='nav nav-tabs flex-row navigation' id="v-tab" role='tablist' aria-orientation="horizontal">
            <button className="nav-link active" id='newFormsTab' data-bs-toggle="tab" role="tab" aria-controls="newForms" aria-selected="true" data-bs-target="#newForms" type="button">
                New
            </button>
            <button className="nav-link" id='inProgressFormsTab' data-bs-toggle="tab" role="tab" aria-controls="inProgressForms" aria-selected="false" data-bs-target="#inProgressForms" type="button">
                In Progress
            </button>
            <button className="nav-link" id='completedFormsTab' data-bs-toggle="tab" role="tab" aria-controls="completedForms" aria-selected="false" data-bs-target="#completedForms" type="button">
                Completed
            </button>
        </div>
        <div className='content tab-content'>
          <div className="newForms tab-pane fade show active" id="newForms" role="tabpanel" aria-labelledby="newFormsTab">
            { formsData.newForms.length === 0 ?
            <div className='noForms'>No New Forms</div>
            :
            <table>
              {tableHeader()}
              <tbody>
              { formsData.newForms.map((form, idx) => (
                <FormsSummaryItem key={idx} formItem={form} />
              ))}
              </tbody>
            </table>
            }
          </div>
          <div className="inProgressForms tab-pane fade" id="inProgressForms" role="tabpanel" aria-labelledby="inProgressFormsTab">
            { formsData.inProgressForms.length === 0 ?
            <div className='noForms'>No Ongoing Forms</div>
            :
            <table>
              {tableHeader()}
              <tbody>
              { formsData.inProgressForms.map((form, idx) => (
                <FormsSummaryItem key={idx} formItem={form} />
              ))}
              </tbody>
            </table>
            }
          </div>
          <div className="completedForms tab-pane fade" id="completedForms" role="tabpanel" aria-labelledby="completedFormsTab">
            { formsData.completedForms.length === 0 ?
            <div className='noForms'>No Completed Forms</div>
            :
            <table>
              {tableHeader()}
              <tbody>
              { formsData.completedForms.map((form, idx) => (
                <FormsSummaryItem key={idx} formItem={form} />
              ))}
              </tbody>
            </table>
            }
          </div>
        </div>
      </div>
      <div className='group'>
        <div className='formsByService'>
          <div className='title1'>Service Distribution</div>
          <CanvasJSChart options = {formsByServiceColumnChartOptions}/>
        </div>
        <div className='formsByServicePercent'>
          <div className='title1'>Service Distribution (%)</div>
          <CanvasJSChart options = {formsByServicePieChartOptions}/>
        </div>
      </div>
    </div>
  )
}
